// Hook for managing Customer Display window
// Used by Staff POS to control the customer-facing display

import { useState, useCallback, useEffect } from 'react'
import { bridge, BridgeNotAvailableError } from '../bridge'
import { useBroadcastChannel, TransactionUpdatePayload, ExchangeRateUpdatePayload } from './useBroadcastChannel'

export function useCustomerDisplay() {
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasExternalDisplay, setHasExternalDisplay] = useState(false)

  const {
    sendTransactionUpdate,
    sendExchangeRateUpdate,
    sendDisplayModeChange,
    sendCounterData,
  } = useBroadcastChannel({
    windowId: 'staff-pos',
  })

  // Check if external display is available (only in Electron)
  useEffect(() => {
    async function checkDisplay() {
      if (!bridge.isElectron) {
        setHasExternalDisplay(false)
        return
      }

      try {
        const { isOpen: open } = await bridge.isCustomerDisplayOpen()
        setIsOpen(open)
        setHasExternalDisplay(true) // If we can check, we have the capability
      } catch {
        setHasExternalDisplay(false)
      }
    }

    checkDisplay()
  }, [])

  // Open customer display window
  const open = useCallback(async (): Promise<boolean> => {
    if (!bridge.isElectron) {
      setError('POS 앱에서만 사용 가능합니다')
      return false
    }

    setIsLoading(true)
    setError(null)

    try {
      const result = await bridge.openCustomerDisplay()
      setIsOpen(result.success)

      if (!result.success) {
        setError(result.message || '고객 화면을 열 수 없습니다')
        return false
      }

      return true
    } catch (err) {
      if (err instanceof BridgeNotAvailableError) {
        setError('POS 앱에서만 사용 가능합니다')
      } else {
        setError((err as Error).message || '고객 화면 열기 실패')
      }
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Close customer display window
  const close = useCallback(async (): Promise<boolean> => {
    if (!bridge.isElectron) return false

    setIsLoading(true)
    setError(null)

    try {
      const result = await bridge.closeCustomerDisplay()
      setIsOpen(!result.success)
      return result.success
    } catch (err) {
      setError((err as Error).message || '고객 화면 닫기 실패')
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Toggle customer display
  const toggle = useCallback(async (): Promise<boolean> => {
    if (isOpen) {
      return close()
    } else {
      return open()
    }
  }, [isOpen, open, close])

  // Send transaction update to customer display
  const updateTransaction = useCallback((payload: TransactionUpdatePayload) => {
    sendTransactionUpdate(payload)
  }, [sendTransactionUpdate])

  // Send exchange rates to customer display
  const updateExchangeRates = useCallback((rates: ExchangeRateUpdatePayload['rates']) => {
    sendExchangeRateUpdate({ rates })
  }, [sendExchangeRateUpdate])

  // Set display mode
  const setDisplayMode = useCallback((mode: 'idle' | 'transaction' | 'complete') => {
    sendDisplayModeChange({ mode })
  }, [sendDisplayModeChange])

  // Send counter data
  const updateCounterData = useCallback((data: {
    currency: string
    totalAmount: number
    denominations: Array<{ denomination: number; count: number }>
    isFinal: boolean
  }) => {
    sendCounterData(data)
  }, [sendCounterData])

  return {
    // State
    isOpen,
    isLoading,
    error,
    hasExternalDisplay,
    isAvailable: bridge.isElectron && hasExternalDisplay,

    // Actions
    open,
    close,
    toggle,

    // Data sync
    updateTransaction,
    updateExchangeRates,
    setDisplayMode,
    updateCounterData,
  }
}

export default useCustomerDisplay
